import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { OtpForm } from '@/features/auth/components/OtpForm'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { ApiError } from '@/shared/api/errors'

type MfaFormProps = {
  mfaToken: string
  onSuccess: () => void
  onAbort: () => void
}

const OTP_LENGTH = 6

export const MfaForm = ({ mfaToken, onSuccess, onAbort }: MfaFormProps) => {
  const { t } = useTranslation()
  const { requestMfaChallenge, submitMfa } = useAuth()
  const [otp, setOtp] = useState('')
  const [phone, setPhone] = useState<string | null>(null)

  const messageForError = (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.status === 401) return t('auth.mfa.errors.invalidCode')
      return t('auth.mfa.errors.generic')
    }
    return t('auth.mfa.errors.network')
  }

  const challenge = useMutation({
    mutationFn: () => requestMfaChallenge(mfaToken),
    onSuccess: data => setPhone(data.obfuscatedPhoneNumber),
  })

  const verify = useMutation({
    mutationFn: () => submitMfa(mfaToken, otp),
    onSuccess: () => onSuccess(),
  })

  useEffect(() => {
    challenge.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaToken])

  return (
    <OtpForm
      title={t('auth.mfa.title')}
      description={
        phone ? t('auth.mfa.sentTo', { phone }) : t('auth.mfa.sentSms')
      }
      inputLabel={t('auth.mfa.inputLabel')}
      length={OTP_LENGTH}
      value={otp}
      onChange={setOtp}
      onSubmit={() => verify.mutate()}
      submitLabel={t('auth.mfa.submitLabel')}
      isSubmitting={verify.isPending}
      error={verify.isError ? messageForError(verify.error) : null}
      errorId='mfa-error'
      secondaryLabel={t('auth.mfa.backToLogin')}
      onSecondary={onAbort}
    />
  )
}
